import * as i0 from '@angular/core';
import { inject, DOCUMENT, PLATFORM_ID, Injectable } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';

// Credit to: https://github.com/DirtyHairy/async-mutex

// Credit to: https://github.com/DirtyHairy/async-mutex

/**
 * Implements a generic queue data structure with optional capacity limit.
 *
 * The Queue class provides methods to enqueue and dequeue items,
 * peek at the first item, check if the queue is empty or full,
 * clear the queue, and get the size of the queue.
 *
 * @template T The type of elements in the queue.
 */
class Queue {
  /**
   * Constructs a new instance of the Queue class with an optional capacity limit.
   *
   * @param {number} [capacity=Infinity] - The maximum number of items the queue can hold. Defaults to Infinity.
   */
  constructor(capacity = Infinity) {
    /**
     * Internal storage for the queue's elements.
     */
    this.collection = [];
    this.capacity = capacity;
  }
  /**
   * Adds an item to the back of the queue.
   *
   * @param {T} item - The item to add to the queue.
   * @throws {Error} If the queue has reached its capacity limit.
   */
  enqueue(item) {
    if (this.size() === this.capacity) {
      throw Error('Max capacity reached!');
    }
    this.collection.push(item);
  }
  /**
   * Removes and returns the item at the front of the queue.
   *
   * @returns {T} The item at the front of the queue.
   */
  dequeue() {
    return this.collection.shift() ?? null;
  }
  /**
   * Returns the item at the front of the queue without removing it.
   *
   * @returns {T} The item at the front of the queue, or null if the queue is empty.
   */
  peek() {
    if (this.size() === 0) {
      return null;
    }
    return this.collection[0];
  }
  /**
   * Removes all items from the queue.
   */
  clear() {
    this.collection = [];
  }
  /**
   * Returns the number of items in the queue.
   *
   * @returns {number} The number of items in the queue.
   */
  size() {
    return this.collection.length;
  }
  /**
   * Checks if the queue is empty.
   *
   * @returns {boolean} True if the queue is empty, false otherwise.
   */
  isEmpty() {
    return this.collection.length === 0;
  }
  /**
   * Checks if the queue has reached its capacity limit.
   *
   * @returns {boolean} True if the queue is full, false otherwise.
   */
  isFull() {
    return this.collection.length === this.capacity;
  }
  /**
   * Returns a string representation of the queue.
   *
   * @returns {string} A JSON string representation of the queue's elements.
   */
  toString() {
    return JSON.stringify(this.collection);
  }
}

// Credit to: https://github.com/DirtyHairy/async-mutex
class Semaphore {
  constructor(maxConcurrency) {
    this.maxConcurrency = maxConcurrency;
    this.queue = new Queue();
    if (maxConcurrency <= 0) {
      throw new Error('Semaphore must be initialized to a positive value.');
    }
    this.value = maxConcurrency;
  }
  acquire() {
    const locked = this.isLocked();
    const ticket = new Promise(r => this.queue.enqueue(r));
    if (!locked) {
      this.dispatch();
    }
    return ticket;
  }
  async runExclusive(callback) {
    const [value, release] = await this.acquire();
    try {
      return await callback(value);
    } finally {
      release();
    }
  }
  isLocked() {
    return this.value <= 0;
  }
  release() {
    if (this.maxConcurrency > 1) {
      throw new Error('This method is unavailable on semaphores with concurrency > 1. Use the scoped release returned by acquire instead.');
    }
    if (this.currentReleaser) {
      const releaser = this.currentReleaser;
      this.currentReleaser = undefined;
      releaser();
    }
  }
  dispatch() {
    const nextConsumer = this.queue.dequeue();
    if (!nextConsumer) {
      return;
    }
    let released = false;
    this.currentReleaser = () => {
      if (released) {
        return;
      }
      released = true;
      this.value++;
      this.dispatch();
    };
    nextConsumer([this.value--, this.currentReleaser]);
  }
}

// Credit to: https://github.com/DirtyHairy/async-mutex
class Mutex {
  constructor() {
    this.semaphore = new Semaphore(1);
  }
  async acquire() {
    const [, releaser] = await this.semaphore.acquire();
    return releaser;
  }
  runExclusive(callback) {
    return this.semaphore.runExclusive(() => callback());
  }
  isLocked() {
    return this.semaphore.isLocked();
  }
  release() {
    this.semaphore.release();
  }
}

// Credit to: https://github.com/DirtyHairy/async-mutex

// Credit to: https://github.com/DirtyHairy/async-mutex
function withTimeout(sync, timeout, timeoutError = new Error('timeout')) {
  return {
    acquire: () =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      let isTimeout = false;
      setTimeout(() => {
        isTimeout = true;
        reject(timeoutError);
      }, timeout);
      const ticket = await sync.acquire();
      if (isTimeout) {
        const release = Array.isArray(ticket) ? ticket[1] : ticket;
        release();
      } else {
        resolve(ticket);
      }
    }),
    async runExclusive(callback) {
      let release = () => undefined;
      try {
        const ticket = await this.acquire();
        if (Array.isArray(ticket)) {
          release = ticket[1];
          return await callback(ticket[0]);
        } else {
          release = ticket;
          return await callback();
        }
      } finally {
        release();
      }
    },
    release() {
      sync.release();
    },
    isLocked: () => sync.isLocked()
  };
}

/**
 * Checks if a given string has a JSON structure.
 *
 * This function determines whether a provided string can be parsed into a JSON object or array.
 * It attempts to parse the string using `JSON.parse` and then checks the result's type.
 * If the parsing is successful and results in either an object or an array, the function returns true.
 * Otherwise, it catches any parsing errors and returns false, indicating the string does not have a JSON structure.
 *
 * @param {string} value - The string to be checked for JSON structure.
 * @returns {boolean} - True if the string has a JSON structure (object or array), false otherwise.
 */
function hasJsonStructure(value) {
  if (typeof value !== 'string') return false;
  try {
    const result = JSON.parse(value);
    const type = Object.prototype.toString.call(result);
    return type === '[object Object]' || type === '[object Array]';
  } catch {
    return false;
  }
}

// CREDIT: Stepan Suvorov and others. See: https://github.com/stevermeister/ngx-cookie-service.
// Functionality expanded to support cookies as JSON objects and to convert cookie values to their actual data-types.
class CookieService {
  constructor() {
    this.document = inject(DOCUMENT);
    this.platformId = inject(PLATFORM_ID);
    this.documentIsAccessible = isPlatformBrowser(this.platformId);
  }
  /**
   * Get cookie Regular Expression - used to locate cookie(s) in Document.
   *
   * @param name The cookie name.
   * @returns A regular expression.
   */
  static getCookieRegExp(name) {
    const escName = name.replace(/([[\]{}()|=;+?,.*^$])/gi, '\\$1');
    return new RegExp('(?:^' + escName + '|;\\s*' + escName + ')=(.*?)(?:;|$)', 'g');
  }
  /**
   * Gets the unencoded version of an encoded component of a Uniform Resource Identifier (URI).
   *
   * @param encodedURIComponent A value representing an encoded URI component.
   * @returns The unencoded version of an encoded component of a Uniform Resource Identifier (URI).
   */
  static safeDecodeURIComponent(encodedURIComponent) {
    try {
      return decodeURIComponent(encodedURIComponent);
    } catch {
      return encodedURIComponent;
    }
  }
  /**
   * Checks if a cookie exists.
   *
   * @param name The cookie name.
   * @returns true/false
   */
  exists(name) {
    if (!this.documentIsAccessible) {
      return false;
    }
    name = encodeURIComponent(name);
    const regExp = CookieService.getCookieRegExp(name);
    return regExp.test(this.document.cookie);
  }
  /**
   * Retrieves a cookie's value by name.
   * @param name The cookie name.
   * @returns The cookie value in the form of its detected type.
   * Ex: JSON objects are returned as Objects, and numbers or booleans stored as strings are returned as numbers or booleans.
   * Use T to assign the data-type to your result.
   */
  get(name) {
    if (this.documentIsAccessible && this.exists(name)) {
      name = encodeURIComponent(name);
      const regExp = CookieService.getCookieRegExp(name);
      const result = regExp.exec(this.document.cookie);
      if (result && result.length > 1) {
        const decoded = CookieService.safeDecodeURIComponent(result[1]);
        if (decoded === 'true') {
          return true;
        }
        if (decoded === 'false') {
          return false;
        }
        const num = parseInt(decoded, 10);
        if (!Number.isNaN(num) && num.toString().length === decoded.length) {
          return num;
        }
        if (hasJsonStructure(decoded)) {
          return JSON.parse(decoded);
        }
        return decoded;
      }
      return null;
    } else {
      return null;
    }
  }
  /**
   * Get cookies by name.
   *
   * @param name The cookie name.
   * @returns The cookie value as a string.
   */
  getRaw(name) {
    if (this.documentIsAccessible && this.exists(name)) {
      name = encodeURIComponent(name);
      const regExp = CookieService.getCookieRegExp(name);
      const result = regExp.exec(this.document.cookie);
      return result && result.length > 1 ? CookieService.safeDecodeURIComponent(result[1]) : '';
    } else {
      return '';
    }
  }
  /**
   * Gets all the cookies.
   * @returns All the cookies as key-value pairs with values returned as strings.
   */
  getAll() {
    if (!this.documentIsAccessible) {
      return {};
    }
    const cookies = {};
    if (this.document.cookie) {
      this.document.cookie.split(';').forEach(currentCookie => {
        const [cookieName, cookieValue] = currentCookie.split('=');
        cookies[CookieService.safeDecodeURIComponent(cookieName.replace(/^ /, ''))] = CookieService.safeDecodeURIComponent(cookieValue);
      });
    }
    return cookies;
  }
  set(name, value, expiresOrOptions, path, domain, secure, sameSite) {
    if (!this.documentIsAccessible) {
      return;
    }
    let cookieValue;
    if (typeof value === 'object') {
      cookieValue = JSON.stringify(value);
    } else {
      cookieValue = String(value);
    }
    if (typeof expiresOrOptions === 'number' || expiresOrOptions instanceof Date || path || domain || secure || sameSite) {
      const optionsBody = {
        expires: expiresOrOptions,
        path,
        domain,
        secure,
        sameSite: sameSite ? sameSite : 'Lax'
      };
      this.set(name, cookieValue, optionsBody);
      return;
    }
    let cookieString = encodeURIComponent(name) + '=' + encodeURIComponent(cookieValue) + ';';
    const options = expiresOrOptions ? expiresOrOptions : {};
    if (options.expires) {
      if (typeof options.expires === 'number') {
        const dateExpires = new Date(new Date().getTime() + options.expires * 1000 * 60 * 60 * 24);
        cookieString += 'expires=' + dateExpires.toUTCString() + ';';
      } else {
        cookieString += 'expires=' + options.expires.toUTCString() + ';';
      }
    }
    if (options.path) {
      cookieString += 'path=' + options.path + ';';
    }
    if (options.domain) {
      cookieString += 'domain=' + options.domain + ';';
    }
    if (options.secure === false && options.sameSite === 'None') {
      options.secure = true;
      console.warn(`[ngx-cookie-service] Cookie ${name} was forced with secure flag because sameSite=None.` + `More details : https://github.com/stevermeister/ngx-cookie-service/issues/86#issuecomment-597720130`);
    }
    if (options.secure) {
      cookieString += 'secure;';
    }
    if (!options.sameSite) {
      options.sameSite = 'Lax';
    }
    cookieString += 'sameSite=' + options.sameSite + ';';
    this.document.cookie = cookieString;
  }
  /**
   * Delete a cookie by name.
   *
   * @param name   The cookie name.
   * @param path   The cookie path.
   * @param domain The cookie domain.
   * @param secure Secure flag.
   * @param sameSite Cookie SameSite flag - https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie/SameSite
   */
  delete(name, path, domain, secure, sameSite = 'Lax') {
    if (!this.documentIsAccessible) {
      return;
    }
    const expiresDate = new Date('Thu, 01 Jan 1970 00:00:01 GMT');
    this.set(name, '', {
      expires: expiresDate,
      path,
      domain,
      secure,
      sameSite
    });
  }
  /**
   * Delete all cookies.
   *
   * @param path   The cookie path.
   * @param domain The cookie domain.
   * @param secure Is the Cookie secure?
   * @param sameSite Is the cookie same site?
   */
  deleteAll(path, domain, secure, sameSite = 'Lax') {
    if (!this.documentIsAccessible) {
      return;
    }
    const cookies = this.getAll();
    for (const cookieName in cookies) {
      if (Object.prototype.hasOwnProperty.call(cookies, cookieName)) {
        this.delete(cookieName, path, domain, secure, sameSite);
      }
    }
  }
  static {
    this.ɵfac = function CookieService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || CookieService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: CookieService,
      factory: CookieService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CookieService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

/**
 * Performs a deep copy of the given source object, array, or primitive.
 *
 * @template T - The type of the source parameter, automatically inferred.
 * @param {T} source - The source item to copy. Can be an object, array, or primitive.
 * @returns {T} - A deep copy of the source item. The return type matches the type of the input.
 */
function copy(source) {
  if (source === null || source === undefined || !Array.isArray(source) && typeof source !== 'object') {
    return source;
  }
  if (Array.isArray(source)) {
    // Create a new array and deep-copy each element
    let target = [];
    source.forEach(src => target = [...target, copy(src)]);
    return target;
  }
  if (typeof source === 'object') {
    let target = {};
    const keys = Object.keys(source);
    for (const key of keys) {
      target = Object.assign({}, target, {
        [key]: copy(source[key])
      });
    }
    return target;
  }
  return source;
}
function flattenArray(multiDimensionalArray, distinctValuesOnly) {
  const flattenedArray = multiDimensionalArray.reduce((flat, toFlatten) => flat.concat(Array.isArray(toFlatten) ? flattenArray(toFlatten) : toFlatten), []);
  if (distinctValuesOnly) {
    return Array.from(new Set(flattenedArray));
  }
  return flattenedArray;
}
/**
 * @deprecated Replaced by new function signature for flattenArray().
 * Flattens a two-dimensional array with the resulting array being of type T[]. Same as flattenArray().
 *
 * @param multiDimensionalArray An array of T.
 */
function flatten2DArray(multiDimensionalArray) {
  const flattenedArray = multiDimensionalArray.reduce((flat, toFlatten) => flat.concat(Array.isArray(toFlatten) ? flattenArray(toFlatten) : toFlatten), []);
  return flattenedArray;
}
/**
 * @deprecated Replaced by new function signature for flattenArray().
 * Flattens a three-dimensional array with the resulting array being of type T[].
 *
 * @param multiDimensionalArray An array of T.
 */
function flatten3DArray(multiDimensionalArray) {
  const flattenedArray = multiDimensionalArray.reduce((flat, toFlatten) => flat.concat(Array.isArray(toFlatten) ? flattenArray(toFlatten) : toFlatten), []);
  return flattenedArray;
}
/**
 * @deprecated Replaced by new function signature for flattenArray().
 * Flattens a four-dimensional array with the resulting array being of type T[].
 *
 * @param multiDimensionalArray An array of T.
 */
function flatten4DArray(multiDimensionalArray) {
  const flattenedArray = multiDimensionalArray.reduce((flat, toFlatten) => flat.concat(Array.isArray(toFlatten) ? flattenArray(toFlatten) : toFlatten), []);
  return flattenedArray;
}
/**
 * @deprecated Replaced by new function signature for flattenArray().
 * Flattens a five-dimensional array with the resulting array being of type T[].
 *
 * @param multiDimensionalArray An array of T.
 */
function flatten5DArray(multiDimensionalArray) {
  const flattenedArray = multiDimensionalArray.reduce((flat, toFlatten) => flat.concat(Array.isArray(toFlatten) ? flattenArray(toFlatten) : toFlatten), []);
  return flattenedArray;
}

/**
 * Groups an array of objects by a specified key.
 * This function iterates over each element in the provided array, applying a key function to determine
 * the grouping property. It then organizes these elements into an object where each property corresponds
 * to a unique key value, and its value is an array of objects that share that key.
 *
 * @template T - The type of the objects in the input array.
 * @param {T[]} list - The array of objects to be grouped.
 * @param {(obj: T) => string | number} key - A function that takes an object and returns a property
 * by which to group the objects, as a string or number.
 * @returns {Record<string, T[]>} - An object where each property is a key group and its value is an
 * array of objects that belong to that group.
 */
function groupBy(list, key) {
  return list.reduce((r, v) => {
    (r[key(v)] || (r[key(v)] = [])).push(v);
    return r;
  }, {});
}

/**
 * @deprecated The method should not be used
 */
function isDeepEqual(object1, object2) {
  if (object1 == null || object2 == null) {
    return false;
  }
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (areObjects && !isDeepEqual(val1, val2) || !areObjects && val1 !== val2) {
      return false;
    }
  }
  return true;
}
function isObject(object) {
  return object != null && typeof object === 'object';
}
function isEqual(val1, val2, ignoreProperties = [], applyIgnorePropertiesToRootObject = true) {
  if (val1 === undefined && val2 === undefined) {
    return true;
  } else if (val1 === null && val2 === null) {
    return true;
  } else if (val1 == null || val2 == null) {
    return false;
  } else if (typeof val1 === 'string' && typeof val2 === 'string') {
    return val1.localeCompare(val2) === 0;
  } else if (Array.isArray(val1) && Array.isArray(val2)) {
    if (val1.length !== val2.length) {
      return false;
    }
    return val1.every((v1, index) => isEqual(v1, val2[index],
    // eslint-disable-next-line prefer-rest-params
    arguments.length > 2 && Array.isArray(arguments[2]) ? ignoreProperties : []));
  } else if (typeof val1 === 'object' && typeof val2 === 'object') {
    const activeIgnoreProperties = applyIgnorePropertiesToRootObject || applyIgnorePropertiesToRootObject === null ? ignoreProperties : [];
    const keys1 = Object.keys(val1).filter(key => !activeIgnoreProperties.includes(key));
    const keys2 = Object.keys(val2).filter(key => !activeIgnoreProperties.includes(key));
    if (!keys1.every(key => keys2.includes(key)) || !keys2.every(key => keys1.includes(key))) {
      return false;
    }
    for (const key of keys1) {
      const propVal1 = val1[key];
      const propVal2 = val2[key];
      if (!isEqual(propVal1, propVal2,
      // eslint-disable-next-line prefer-rest-params
      arguments.length > 2 && Array.isArray(arguments[2]) ? ignoreProperties : [])) {
        return false;
      }
    }
    return true;
  } else if (typeof val1 === 'number' && typeof val2 === 'number' || typeof val1 === 'bigint' && typeof val2 === 'bigint' || typeof val1 === 'boolean' && typeof val2 === 'boolean') {
    return val1 === val2;
  } else if (typeof val1 === 'function' && typeof val2 === 'function') {
    return val1() === val2();
  }
  return false;
}

/**
 * Provides a service for observing and managing a collection of items.
 * This service allows components and services to subscribe to item updates and removals.
 *
 * @template T - The type of the items being observed.
 */
class ItemObserver {
  constructor() {
    /**
     * A subject that emits the current list of items whenever an item is updated.
     */
    this.itemsUpdatedSubject = new Subject();
    /**
     * An observable that components can subscribe to in order to get updates about item changes.
     */
    this.itemsUpdated$ = this.itemsUpdatedSubject.asObservable();
    /**
     * A subject that emits the current list of items whenever an item is removed.
     */
    this.itemsRemovedSubject = new Subject();
    /**
     * An observable that components can subscribe to in order to get updates about item removals.
     */
    this.itemsRemoved$ = this.itemsRemovedSubject.asObservable();
  }
  /**
   * Updates a single item and notifies subscribers about the change.
   *
   * @param {T} item - The item to update.
   */
  updateItem(item) {
    this.itemsUpdatedSubject.next([item]);
  }
  /**
   * Updates multiple items and notifies subscribers about the change.
   *
   * @param {T[]} items - The items to update.
   */
  updateItems(items) {
    this.itemsUpdatedSubject.next(items);
  }
  /**
   * Removes a single item and notifies subscribers about the removal.
   *
   * @param {T} item - The item to remove.
   */
  removeItem(item) {
    this.itemsRemovedSubject.next([item]);
  }
  /**
   * Removes multiple items and notifies subscribers about the removal.
   *
   * @param {T[]} items - The items to remove.
   */
  removeItems(items) {
    this.itemsRemovedSubject.next(items);
  }
  static {
    this.ɵfac = function ItemObserver_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ItemObserver)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ItemObserver,
      factory: ItemObserver.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ItemObserver, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

/**
 * Decodes a JWT token to retrieve its payload.
 *
 * This function takes an encoded JWT token, splits it by the '.' character to isolate the payload,
 * decodes it from base64url format, and then parses the JSON string to return the payload as an object.
 *
 * @param {string} encodedToken - The JWT token encoded in base64url format.
 * @returns {T} - The decoded payload of the JWT token as an object.
 * @template T - The type of the decoded payload.
 */
function jwtDecode(encodedToken) {
  const stringifiedToken = decode(encodedToken.split('.')[1]);
  return JSON.parse(stringifiedToken);
}
/**
 * Decodes a base64url encoded string to a regular string.
 *
 * This function replaces URL-safe base64url characters with their respective base64 characters,
 * adds the required '=' padding based on the string length, and then decodes it from base64.
 * If the base64 decoding fails, it attempts to decode it as base64url.
 *
 * @param {string} str - The base64url encoded string.
 * @returns {string} - The decoded string.
 * @throws {Error} - Throws an error if the input string has an illegal base64url format.
 */
function decode(str) {
  let output = str.replace(/-/g, '+').replace(/_/g, '/');
  switch (output.length % 4) {
    case 0:
      break;
    case 2:
      output += '==';
      break;
    case 3:
      output += '=';
      break;
    default:
      throw new Error('Illegal base64url string!');
  }
  try {
    return b64DecodeUnicode(output);
  } catch {
    return atob(output);
  }
}
/**
 * Decodes a base64 encoded string to a UTF-8 string.
 *
 * This function decodes a base64 encoded string to a binary string, then encodes the binary string
 * to a URI component, and finally decodes the URI component to a UTF-8 string.
 *
 * @param {string} str - The base64 encoded string.
 * @returns {string} - The decoded UTF-8 string.
 */
function b64DecodeUnicode(str) {
  return decodeURIComponent(atob(str).replace(/(.)/g, function (m, p) {
    let code = p.charCodeAt(0).toString(16).toUpperCase();
    if (code.length < 2) {
      code = '0' + code;
    }
    return '%' + code;
  }));
}

/**
 * Generic List class implementing IList interface.
 * Provides methods to manipulate a collection of items with optional capacity limits.
 */
class List {
  /**
   * Creates an instance of List.
   * @param {number} [capacity=Infinity] - The maximum number of items the list can contain. Defaults to Infinity.
   */
  constructor(capacity = Infinity) {
    this.collection = []; // Internal array to hold list items.
    this.capacity = capacity;
  }
  /**
   * Gets the items in the list.
   * @returns {T[]} An array of list items.
   */
  get items() {
    return this.collection;
  }
  /**
   * Retrieves the item at the specified index.
   * @param {number} index - The zero-based index of the item to retrieve.
   * @returns {T} The item at the specified index.
   * @throws {Error} If the index is out of bounds.
   */
  itemAt(index) {
    if (index < 0 || index > this.collection.length - 1) {
      throw new Error('Invalid index');
    }
    return this.collection[index];
  }
  /**
   * Retrieves the first item in the list.
   * @returns {T} The first item in the list, or null if the list is empty.
   */
  first() {
    if (this.collection.length === 0) {
      return null;
    }
    return this.collection[0];
  }
  /**
   * Retrieves the last item in the list.
   * @returns {T} The last item in the list, or null if the list is empty.
   */
  last() {
    if (this.collection.length === 0) {
      return null;
    }
    return this.collection[this.collection.length - 1];
  }
  /**
   * Adds a new item to the end of the list.
   * @param {T} item - The item to add.
   * @throws {Error} If adding the item would exceed the list's capacity.
   */
  add(item) {
    if (this.size() === this.capacity) {
      throw Error('Max capacity reached.');
    }
    this.collection.push(item);
  }
  /**
   * Adds multiple items to the end of the list.
   * @param {...T[]} items - The items to add.
   * @throws {Error} If adding the items would exceed the list's capacity.
   */
  addMany(...items) {
    if (this.size() + items.length >= this.capacity + 1) {
      throw Error('The number of items added would surpass the max capacity.');
    }
    this.collection.push(...items);
  }
  /**
   * Inserts an item at the specified index.
   * @param {number} index - The zero-based index at which to insert the item.
   * @param {T} item - The item to insert.
   * @throws {Error} If the index is out of bounds.
   */
  insertAt(index, item) {
    if (index < 0 || index > this.collection.length - 1) {
      throw new Error('Invalid index');
    }
    this.collection = [...this.collection.slice(0, index), item, ...this.collection.slice(index)];
  }
  /**
   * Inserts an item after another specified item.
   * @param {T} afterItem - The item after which to insert the new item.
   * @param {T} item - The item to insert.
   * @returns {number} The index at which the item was inserted.
   */
  insertAfter(afterItem, item) {
    const index = this.collection.indexOf(afterItem);
    this.insertAt(index + 1, item);
    return index + 1;
  }
  /**
   * Inserts an item before another specified item.
   * @param {T} beforeItem - The item before which to insert the new item.
   * @param {T} item - The item to insert.
   * @returns {number} The index at which the item was inserted.
   */
  insertBefore(beforeItem, item) {
    const index = this.collection.indexOf(beforeItem);
    this.insertAt(index, item);
    return index;
  }
  /**
   * Replaces an existing item with a new item.
   * @param {T} replaceItem - The item to be replaced.
   * @param {T} item - The new item.
   * @returns {number} The index at which the item was replaced.
   */
  replace(replaceItem, item) {
    const index = this.collection.indexOf(replaceItem);
    this.replaceAt(index, item);
    return index;
  }
  /**
   * Replaces the item at the specified index.
   * @param {number} index - The zero-based index of the item to replace.
   * @param {T} item - The new item.
   * @throws {Error} If the index is out of bounds.
   */
  replaceAt(index, item) {
    if (index < 0 || index > this.collection.length - 1) {
      throw new Error('Invalid index');
    }
    if (index > -1) {
      this.collection = [...this.collection.slice(0, index), item, ...this.collection.slice(index + 1)];
    }
  }
  /**
   * Removes a specified item from the list.
   * @param {T} item - The item to remove.
   */
  remove(item) {
    const index = this.collection.indexOf(item);
    this.removeAt(index);
  }
  /**
   * Removes the item at the specified index.
   */
  removeAt(index) {
    if (index < 0 || index > this.collection.length - 1) {
      throw new Error('Invalid index');
    }
    if (index > -1) {
      this.collection = [...this.collection.slice(0, index), ...this.collection.slice(index + 1)];
    }
  }
  /**
   * Removes multiple items from the list.
   * @param items
   */
  removeMany(...items) {
    items.forEach(item => this.remove(item));
  }
  /**
   * Indicates whether the list contains a specified item.
   */
  contains(item) {
    return this.collection.indexOf(item) > -1;
  }
  /**
   * Determines whether an element is in the list.
   * @param predicate {function} A function that defines the conditions of the element to search for.
   * @param thisArg {any} An object to which the `this` keyword can refer in the predicate function.
   * @returns {boolean} true if the element is found in the list; otherwise, false.
   */
  includes(predicate, thisArg) {
    return this.collection.some(predicate, thisArg);
  }
  /**
   * Searches for the specified object and returns the zero-based index of the first occurrence within the list.
   * @param item {T} The object to locate in the list.
   * @returns {number} The zero-based index of the first occurrence of item within the list, if found; otherwise, -1.
   */
  indexOf(item) {
    return this.collection.indexOf(item);
  }
  /**
   * Removes all elements from the list.
   */
  clear() {
    this.collection = [];
  }
  /**
   * Returns the number of items in the list.
   */
  size() {
    return this.collection.length;
  }
  /**
   * Indicates whether the list is empty.
   */
  isEmpty() {
    return this.collection.length === 0;
  }
  /**
   * Indicates whether the list is full.
   */
  isFull() {
    return this.collection.length === this.capacity;
  }
  /**
   * Returns a JSON string representation of the list.
   */
  toString() {
    return JSON.stringify(this.collection);
  }
}
class CodedError {
  constructor(errorCode, description) {
    this.errorCode = errorCode;
    this.description = description;
  }
}

/**
 * Manages a pool of data items with a capacity limit, emitting the pool of items
 * at a regular interval or when the capacity is reached.
 *
 * This class uses a mutex to ensure thread-safe access to the pool, allowing
 * data items to be added concurrently without race conditions. It emits the
 * current pool of items either when the pool reaches its capacity or after a
 * specified time interval, whichever comes first.
 *
 * @template T The type of elements in the pool.
 */
class PoolingManager {
  /**
   * Constructs a new instance of the PoolingManager class.
   *
   * @param {number} [capacity=25] - The maximum number of items the pool can hold. Defaults to 25.
   * @param {number} [emitMs=2500] - The interval in milliseconds between automatic emissions of the pool. Defaults to 2500.
   */
  constructor(capacity = 25, emitMs = 2500) {
    /**
     * A mutex to ensure thread-safe access to the pool.
     */
    this.mutex = new Mutex();
    /**
     * The current pool of items.
     */
    this.pool = [];
    /**
     * Identifier for the timer that manages automatic emissions.
     */
    this.timerId = null;
    /**
     * A subject that emits the current pool of items.
     */
    this.dataSubject = new Subject();
    /**
     * An observable that subscribers can use to receive the current pool of items.
     */
    this.data$ = this.dataSubject.asObservable();
    this.capacity = capacity;
    this.emitMs = emitMs;
  }
  /**
   * Adds a new item to the pool and manages the emission of the pool.
   *
   * @param {T} data - The item to add to the pool.
   */
  add(data) {
    this.manageDataPool(data);
  }
  /**
   * Disposes of the current pool and clears the timer.
   */
  dispose() {
    if (this.timerId) {
      clearTimeout(this.timerId);
    }
    this.pool = [];
  }
  /**
   * Manages the pool of data items, adding new items to the pool and
   * emitting the pool when necessary.
   *
   * This method is thread-safe, ensuring that items are added to the pool
   * and the pool is emitted without race conditions.
   *
   * @param {T} data - The item to add to the pool.
   * @private
   */
  async manageDataPool(data) {
    const release = await this.mutex.acquire();
    this.pool.push(data);
    release();
    if (this.pool.length >= this.capacity) {
      await this.emitPool();
    }
    if (this.timerId == null) {
      this.timerId = setTimeout(async () => await this.emitPool(), this.emitMs);
    }
  }
  /**
   * Emits the current pool of items and clears the pool.
   *
   * This method is thread-safe, ensuring that the pool is emitted and cleared
   * without race conditions.
   *
   * @private
   */
  async emitPool() {
    const release = await this.mutex.acquire();
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.timerId = null;
    if (this.pool.length > 0) {
      this.dataSubject.next(this.pool);
      this.pool = [];
    }
    release();
  }
}

/**
 * Regular expression for validating if a string contains only numbers.
 * It allows a sequence of digits and optionally a single digit after them.
 *
 * Example matches:
 * - "12345"
 * - "67890"
 *
 * Example non-matches:
 * - "12345a"
 * - "12 345"
 *
 * @type {RegExp}
 */
const numbersOnly = new RegExp('^[0-9]+$', 'g');
function sort(list, field, direction = 'ascending') {
  return list.sort((item1, item2) => {
    if (item1[field] === null) {
      return -1; // nulls first
    }
    if (item2[field] === null) {
      return 1; // nulls first
    }
    if (typeof item1[field] === 'number' || typeof item1[field] === 'bigint') {
      return (direction === 'ascending' ? 1 : -1) * (item1[field] - item2[field]);
    } else if (typeof item1[field] === 'string') {
      return (direction === 'ascending' ? 1 : -1) * item1[field].localeCompare(item2[field]);
    } else if (item1[field] instanceof Date) {
      return (direction === 'ascending' ? 1 : -1) * (+item1[field] - +item2[field]);
    } else if (typeof item1[field] === 'boolean') {
      return (direction === 'ascending' ? 1 : -1) * (item1[field] === item2[field] ? 0 : item1[field] ? 1 : -1);
    } else {
      return 0;
    }
  });
}

/**
 * Represents a sorted list that can contain elements of type string, number, or any object that implements the IComparable interface.
 * The list maintains its elements in sorted order at all times.
 */
class SortedList {
  /**
   * Initializes a new instance of the SortedList class.
   * @param capacity {number} The maximum number of elements the list can hold. Defaults to Infinity.
   */
  constructor(capacity = Infinity) {
    this.collection = [];
    this.capacity = capacity;
  }
  /**
   * Gets the elements of the list.
   * @returns {T[]} An array of the elements in the list.
   */
  get items() {
    return this.collection;
  }
  /**
   * Retrieves the element at the specified index.
   * @param index {number} The zero-based index of the element to retrieve.
   * @returns {T} The element at the specified index.
   * @throws {Error} Throws an error if the index is out of range.
   */
  itemAt(index) {
    if (index < 0 || index > this.collection.length - 1) {
      throw new Error('Invalid index');
    }
    return this.collection[index];
  }
  /**
   * Retrieves the first element in the list.
   * @returns {T} The first element in the list or null if the list is empty.
   */
  first() {
    if (this.collection.length === 0) {
      return null;
    }
    return this.collection[0];
  }
  /**
   * Retrieves the last element in the list.
   * @returns {T} The last element in the list or null if the list is empty.
   */
  last() {
    if (this.collection.length === 0) {
      return null;
    }
    return this.collection[this.collection.length - 1];
  }
  /**
   * Adds an element to the list in sorted order.
   * @param item {T} The element to add to the list.
   * @throws {Error} Throws an error if adding the element would exceed the list's capacity.
   */
  add(item) {
    if (this.size() === this.capacity) {
      throw Error('Max capacity reached!');
    }
    let collectionUpdated = false;
    if (this.collection.length > 0) {
      for (let index = 0; index < this.collection.length; index++) {
        if (typeof item === 'number' || typeof item === 'string') {
          if (item < this.collection[index]) {
            this.collection = [...this.collection.slice(0, index), item, ...this.collection.slice(index)];
            collectionUpdated = true;
            break;
          }
        } else {
          if (item.compareTo(this.collection[index]) === -1) {
            this.collection = [...this.collection.slice(0, index), item, ...this.collection.slice(index)];
            collectionUpdated = true;
            break;
          }
        }
      }
    }
    if (!collectionUpdated) {
      this.collection.push(item);
    }
  }
  /**
   * Adds multiple elements to the list.
   * @param items {T[]} The elements to add to the list.
   */
  addMany(...items) {
    items.forEach(item => this.add(item));
  }
  /**
   * Removes the first occurrence of a specific element from the list.
   * @param item {T} The element to remove from the list.
   */
  remove(item) {
    const index = this.collection.indexOf(item);
    this.removeAt(index);
  }
  /**
   * Removes the element at the specified index of the list.
   * @param index {number} The zero-based index of the element to remove.
   * @throws {Error} Throws an error if the index is out of range.
   */
  removeAt(index) {
    if (index < 0 || index > this.collection.length - 1) {
      throw new Error('Invalid index');
    }
    if (index > -1) {
      this.collection = [...this.collection.slice(0, index), ...this.collection.slice(index + 1)];
    }
  }
  /**
   * Removes all the occurrences of the specified elements from the list.
   * @param items {T[]} The elements to remove from the list.
   */
  removeMany(...items) {
    items.forEach(item => this.remove(item));
  }
  /**
   * Determines whether an element is in the list.
   * @param item {T} The element to locate in the list.
   * @returns {boolean} true if item is found in the list; otherwise, false.
   */
  contains(item) {
    return this.collection.indexOf(item) > -1;
  }
  /**
   * Determines whether the list includes any elements that pass a test.
   * @param predicate {Function} A function to test each element for a condition.
   * @param thisArg {any} An object to which the this keyword can refer in the predicate function.
   * @returns {boolean} true if any elements pass the test; otherwise, false.
   */
  includes(predicate, thisArg) {
    return this.collection.some(predicate, thisArg);
  }
  /**
   * Searches for the specified object and returns the zero-based index of the first occurrence within the list.
   * @param item {T} The object to locate in the list.
   * @returns {number} The zero-based index of the first occurrence of item within the list, if found; otherwise, -1.
   */
  indexOf(item) {
    return this.collection.indexOf(item);
  }
  /**
   * Removes all elements from the list.
   */
  clear() {
    this.collection = [];
  }
  /**
   * Gets the number of elements contained in the list.
   */
  size() {
    return this.collection.length;
  }
  /**
   * Determines whether the list is empty.
   */
  isEmpty() {
    return this.collection.length === 0;
  }
  /**
   * Determines whether the list is full.
   */
  isFull() {
    return this.collection.length === this.capacity;
  }
  /**
   * Returns a JSON string that represents the current object.
   */
  toString() {
    return JSON.stringify(this.collection);
  }
}

/**
 * Unsubscribes from a list of Subscriptions.
 *
 * This function takes any number of RxJS Subscription objects and unsubscribes from each.
 * It's a utility function to help with clean unsubscribing from observables in components or services
 * to prevent memory leaks.
 *
 * @param subscriptions - A rest parameter that takes multiple Subscription objects.
 */
function unsubscribe(...subscriptions) {
  if (subscriptions != null) {
    subscriptions.forEach(subscription => {
      if (subscription != null) {
        subscription.unsubscribe();
      }
    });
  }
}
function generateUUID(sets = 8) {
  const stringArr = [];
  for (let i = 0; i < sets; i++) {
    const S4 = ((1 + Math.random()) * 0x10000 | 0).toString(16).substring(1);
    stringArr.push(S4);
  }
  return stringArr.join('-');
}

/**
 * Generated bundle index. Do not edit.
 */

export { CodedError, CookieService, ItemObserver, List, Mutex, PoolingManager, Queue, Semaphore, SortedList, copy, flatten2DArray, flatten3DArray, flatten4DArray, flatten5DArray, flattenArray, generateUUID, groupBy, hasJsonStructure, isDeepEqual, isEqual, jwtDecode, numbersOnly, sort, unsubscribe, withTimeout };
